/**
 * Based on long-press-event by @author John Doherty
 * https://github.com/john-doherty/long-press-event
 */

// local timer object based on rAF
let timer = null;

// check if we're using a touch screen
const isTouch = "ontouchstart" in window;

// switch to touch events if using a touch screen
const mouseDown = isTouch ? "touchstart" : "mousedown";
const mouseUp = isTouch ? "touchend" : "mouseup";
const mouseMove = isTouch ? "touchmove" : "mousemove";

// track number of pixels the mouse moves during long press
let startX = 0; // mouse x position when timer started
let startY = 0; // mouse y position when timer started
let maxDiffX = 10; // max number of X pixels the mouse can move during long press before it is canceled
let maxDiffY = 10; // max number of Y pixels the mouse can move during long press before it is canceled

let delay = 1000; // number of milliseconds to touch before firing the long press event

/**
 * Behaves the same as setTimeout except uses requestAnimationFrame() where possible for better performance
 * @param {function} fn The callback function
 * @param {int} delay The delay in milliseconds
 * @returns {object} handle to the timeout object
 */
function requestTimeout(fn, delay) {
    const start = new Date().getTime();
    const handle = { value: null };

    const loop = function () {
        const current = new Date().getTime();
        const delta = current - start;

        if (delta >= delay) {
            fn.call();
        } else {
            handle.value = requestAnimationFrame(loop);
        }
    };

    handle.value = requestAnimationFrame(loop);

    return handle;
}

/**
 * Behaves the same as clearTimeout except uses cancelRequestAnimationFrame() where possible for better performance
 * @param {object} handle The callback function
 * @returns {void}
 */
function clearRequestTimeout(handle) {
    if (handle) {
        cancelAnimationFrame(handle.value);
    }
}

/**
 * Gets event object regardless of touch or regular event
 * @param {object} e - default browser event object
 * @returns {object} regular event object
 */
function normaliseEvent(e) {
    if (e.touches && e.touches[0]) {
        return e.touches[0];
    }

    return e;
}

/**
 * Fires the 'long-press' event on element
 * @param {MouseEvent|TouchEvent} originalEvent The original event being fired
 * @returns {void}
 */
function fireLongPressEvent(originalEvent) {
    clearLongPressTimer();

    originalEvent = normaliseEvent(originalEvent);

    const allowClickEvent = this.dispatchEvent(
        new CustomEvent("uicLongPress", {
            bubbles: true,
            cancelable: true,
            detail: {
                clientX: originalEvent.clientX,
                clientY: originalEvent.clientY,
                offsetX: originalEvent.offsetX,
                offsetY: originalEvent.offsetY,
                pageX: originalEvent.pageX,
                pageY: originalEvent.pageY,
                screenX: originalEvent.screenX,
                screenY: originalEvent.screenY,
            },
        })
    );

    if (!allowClickEvent) {
        // supress the next click event if e.preventDefault() was called in long-press handler
        document.addEventListener(
            "click",
            function suppressEvent(e) {
                document.removeEventListener("click", suppressEvent, true);
                cancelEvent(e);
            },
            true
        );
    }
}

/**
 * method responsible for starting the long press timer
 * @param {event} e - event object
 * @returns {void}
 */
function startLongPressTimer(e) {
    clearLongPressTimer();

    const el = e.target;
    const longPressDelayInMs = parseInt(
        getNearestAttribute(el, "data-long-press-delay", delay),
        10
    );

    // start the timer
    timer = requestTimeout(fireLongPressEvent.bind(el, e), longPressDelayInMs);
}

/**
 * Responsible for clearing a pending long press timer
 */
function clearLongPressTimer() {
    clearRequestTimeout(timer);
    timer = null;
}

/**
 * Cancels the current event
 * @param {object} e - browser event object
 * @returns {void}
 */
function cancelEvent(e) {
    e.stopImmediatePropagation();
    e.preventDefault();
    e.stopPropagation();
}

/**
 * Starts the timer on mouse down and logs current position
 * @param {object} e - browser event object
 * @returns {void}
 */
function touchStartHandler(e) {
    startX = e.clientX;
    startY = e.clientY;
    startLongPressTimer(e);
}

/**
 * If the mouse moves n pixels during long-press, cancel the timer
 * @param {object} e - browser event object
 * @returns {void}
 */
function touchMoveHandler(e) {
    // calculate total number of pixels the pointer has moved
    const diffX = Math.abs(startX - e.clientX);
    const diffY = Math.abs(startY - e.clientY);

    // if pointer has moved more than allowed, cancel the long-press timer and therefore the event
    if (diffX >= maxDiffX || diffY >= maxDiffY) {
        clearLongPressTimer();
    }
}

/**
 * Gets attribute off HTML element or nearest parent
 * @param {object} el - HTML element to retrieve attribute from
 * @param {string} attributeName - name of the attribute
 * @param {any} defaultValue - default value to return if no match found
 * @returns {any} attribute value or defaultValue
 */
function getNearestAttribute(el, attributeName, defaultValue) {
    // walk up the dom tree looking for data-action and data-trigger
    while (el && el !== document.documentElement) {
        const attributeValue = el.getAttribute(attributeName);

        if (attributeValue) {
            return attributeValue;
        }

        el = el.parentNode;
    }

    return defaultValue;
}

// hook events that clear a pending long press event
document.addEventListener(mouseUp, clearLongPressTimer, true);
document.addEventListener("touchmove", touchMoveHandler, true);
document.addEventListener(mouseMove, clearLongPressTimer, true);
document.addEventListener("wheel", clearLongPressTimer, true);
document.addEventListener("scroll", clearLongPressTimer, true);

// hook events that can trigger a long press event
document.addEventListener(mouseDown, touchStartHandler, true); // <- start
